import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Menu, X, LogOut, LayoutDashboard, Shield, Linkedin } from "lucide-react";

const links = [
  { to: "/tools", label: "Tools" },
  { to: "/jobs", label: "Jobs" },
  { to: "/events", label: "Events" },
  { to: "/courses", label: "Courses" },
  { to: "/news", label: "News" },
  { to: "/papers", label: "Papers", badge: "New" },
  { to: "/side-hustles", label: "Side Hustles" },
  { to: "/ai-human", label: "AI Human", badge: "New" },
  { to: "/ai-progress", label: "AI Data & Statistics" },
  { to: "/ai-in-your-job", label: "AI in Your Job" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  return (
    <header className="glass-header sticky top-0 z-50" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
          <svg width="34" height="34" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" className="shrink-0" aria-hidden="true">
            <rect x="3" y="3" width="42" height="42" rx="12" fill="#35D0E0" />
            <circle cx="24" cy="24" r="15" stroke="#0B1220" strokeWidth="1.5" opacity="0.22" />
            <path d="M11 16l4 16 5-12 5 12 4-16" stroke="#0B1220" strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" fill="none" />
          </svg>
          <span className="font-display font-extrabold text-lg tracking-tight">
            World<span className="text-[#00F0FF]">AI</span>Info
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`relative text-sm font-medium transition-colors hover:text-[#00F0FF] ${loc.pathname === l.to ? "text-[#00F0FF]" : "text-[#94A3B8]"}`}>
              {l.label}
              {l.badge && (
                <span className="ml-1.5 align-middle text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#00F0FF]/15 text-[#00F0FF] border border-[#00F0FF]/30">
                  {l.badge}
                </span>
              )}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a href="https://www.linkedin.com/company/worldaiinfo/" target="_blank" rel="noopener noreferrer"
            aria-label="Follow WorldAIInfo on LinkedIn" data-testid="nav-linkedin"
            className="text-[#94A3B8] hover:text-[#00F0FF] transition-colors">
            <Linkedin className="w-5 h-5" />
          </a>
          {user ? (
            <>
              {user.role === "admin" && (
                <Link to="/admin" data-testid="nav-admin" className="flex items-center gap-1.5 text-sm text-[#94A3B8] hover:text-[#00F0FF]">
                  <Shield className="w-4 h-4" /> Admin
                </Link>
              )}
              <Link to="/dashboard" data-testid="nav-dashboard" className="flex items-center gap-1.5 text-sm text-[#94A3B8] hover:text-[#00F0FF]">
                <LayoutDashboard className="w-4 h-4" /> Dashboard
              </Link>
              <button onClick={() => { logout(); nav("/"); }} data-testid="nav-logout"
                className="flex items-center gap-1.5 text-sm text-[#94A3B8] hover:text-[#EF4444]">
                <LogOut className="w-4 h-4" /> Logout
              </button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className="text-sm text-[#94A3B8] hover:text-white">Sign in</Link>
              <Link to="/register" data-testid="nav-register"
                className="text-sm font-semibold bg-[#00F0FF] text-[#04121a] px-4 py-2 rounded-md hover:brightness-110 transition">
                List with us
              </Link>
            </>
          )}
        </div>

        <button className="md:hidden text-white" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-white/10 px-6 py-4 flex flex-col gap-4 bg-[#0B0F19]">
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="text-[#94A3B8]">{l.label}</Link>
          ))}
          {user ? (
            <>
              <Link to="/dashboard" onClick={() => setOpen(false)} className="text-[#94A3B8]">Dashboard</Link>
              {user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} className="text-[#94A3B8]">Admin</Link>}
              <button onClick={() => { logout(); setOpen(false); nav("/"); }} className="text-left text-[#EF4444]">Logout</button>
            </>
          ) : (
            <>
              <Link to="/login" onClick={() => setOpen(false)} className="text-[#94A3B8]">Sign in</Link>
              <Link to="/register" onClick={() => setOpen(false)} className="text-[#00F0FF]">List with us</Link>
            </>
          )}
          <a href="https://www.linkedin.com/company/worldaiinfo/" target="_blank" rel="noopener noreferrer"
            onClick={() => setOpen(false)} data-testid="nav-linkedin-mobile"
            className="inline-flex items-center gap-2 text-[#94A3B8]">
            <Linkedin className="w-4 h-4" /> LinkedIn
          </a>
        </div>
      )}
    </header>
  );
}
